import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FiAlertCircle, FiX } from "react-icons/fi";
import {
  coaService,
  normalizeAccountType,
  ACCOUNT_NORMAL_SIDE,
} from "../../services/coaService";
import { useClient } from "../../context/ClientContext";

export const ACCOUNT_TYPES = [
  { value: "Asset", label: "Assets" },
  { value: "Liability", label: "Liabilities" },
  { value: "Equity", label: "Equity" },
  { value: "Income", label: "Income" },
  { value: "Expense", label: "Expenses" },
];

export const SUB_TYPES = {
  Asset: [
    "Cash and Bank",
    "Money in Transit",
    "Accounts Receivable",
    "Expected Payments from Customers",
    "Inventory",
    "Prepaid Expenses",
    "Fixed Assets",
    "Property, Plant, Equipment",
    "Accumulated Depreciation",
    "Depreciation and Amortization",
    "Vendor Prepayments and Vendor Credits",
    "Other Current Asset",
    "Other Short-Term Asset",
    "Other Long-Term Asset",
  ],
  Liability: [
    "Credit Card",
    "Accounts Payable",
    "Expected Payments to Vendors",
    "Loan and Line of Credit",
    "Taxes Payable",
    "Payroll Liabilities",
    "Due For Payroll",
    "Due to You and Other Business Owners",
    "Customer Prepayments and Customer Credits",
    "Other Current Liability",
    "Other Short-Term Liability",
    "Other Long-Term Liability",
  ],
  Equity: [
    "Owner Capital",
    "Owner Drawings",
    "Business Owner Contribution and Drawing",
    "Retained Earnings",
    "Retained Earnings: Profit",
    "Other Equity",
  ],
  Income: [
    "Sales Revenue",
    "Service Revenue",
    "Income",
    "Discount",
    "Other Income",
  ],
  Expense: [
    "Operating Expense",
    "Other Expense",
    "Cost of Goods Sold",
    "Payment Processing Fee",
    "Payroll Expense",
    "Depreciation Expense",
    "Interest Expense",
  ],
};
// Compatibility with any older components that index SUB_TYPES.Expenses.
SUB_TYPES.Expenses = SUB_TYPES.Expense;

const CURRENCY_GROUPS = [
  {
    label: "Americas",
    items: [
      ["USD", "US Dollar"],
      ["CAD", "Canadian Dollar"],
      ["MXN", "Mexican Peso"],
      ["BRL", "Brazilian Real"],
    ],
  },
  {
    label: "Europe",
    items: [
      ["EUR", "Euro"],
      ["GBP", "British Pound"],
      ["CHF", "Swiss Franc"],
      ["SEK", "Swedish Krona"],
      ["NOK", "Norwegian Krone"],
    ],
  },
  {
    label: "Asia Pacific",
    items: [
      ["PHP", "Philippine Peso"],
      ["AUD", "Australian Dollar"],
      ["NZD", "New Zealand Dollar"],
      ["JPY", "Japanese Yen"],
      ["CNY", "Chinese Yuan"],
      ["HKD", "Hong Kong Dollar"],
      ["SGD", "Singapore Dollar"],
      ["INR", "Indian Rupee"],
      ["KRW", "South Korean Won"],
      ["IDR", "Indonesian Rupiah"],
      ["MYR", "Malaysian Ringgit"],
      ["THB", "Thai Baht"],
      ["VND", "Vietnamese Dong"],
    ],
  },
  {
    label: "Middle East & Africa",
    items: [
      ["AED", "UAE Dirham"],
      ["SAR", "Saudi Riyal"],
      ["QAR", "Qatari Riyal"],
      ["ZAR", "South African Rand"],
      ["NGN", "Nigerian Naira"],
      ["EGP", "Egyptian Pound"],
      ["KES", "Kenyan Shilling"],
    ],
  },
];

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100";
const labelClass = "mb-1.5 block text-xs font-semibold text-slate-600";

const blankForm = (type, currency, subType) => ({
  code: "",
  name: "",
  type,
  sub_type: subType || SUB_TYPES[type]?.[0] || "",
  currency: currency || "USD",
  description: "",
});

export default function COAModal({
  isOpen,
  onClose,
  onSuccess,
  initialData,
  defaultType = "Asset",
  defaultSubType = null,
}) {
  const { activeClient } = useClient();
  const organizationId = activeClient?.id;
  const [formData, setFormData] = useState(() => blankForm("Asset", "USD"));
  const [organizationCurrency, setOrganizationCurrency] = useState("USD");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !organizationId) return;
    let cancelled = false;
    coaService
      .getOrganizationCurrency(organizationId)
      .then((currency) => {
        if (!cancelled) setOrganizationCurrency(currency);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, organizationId]);

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    if (initialData) {
      const type = normalizeAccountType(initialData.type);
      setFormData({
        code: initialData.code || "",
        name: initialData.name || "",
        type,
        sub_type: initialData.sub_type || SUB_TYPES[type]?.[0] || "",
        currency: initialData.currency || organizationCurrency,
        description: initialData.description || "",
      });
    } else {
      const type = normalizeAccountType(defaultType);
      const validType = SUB_TYPES[type] ? type : "Asset";
      setFormData(blankForm(validType, organizationCurrency, defaultSubType));
    }
  }, [isOpen, initialData, defaultType, defaultSubType, organizationCurrency]);

  const detailTypes = useMemo(() => {
    const list = SUB_TYPES[formData.type] || [];
    return formData.sub_type && !list.includes(formData.sub_type)
      ? [...list, formData.sub_type]
      : list;
  }, [formData.type, formData.sub_type]);

  const currencyExists = CURRENCY_GROUPS.some((g) =>
    g.items.some(([code]) => code === formData.currency),
  );
  const update = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    if (!organizationId) {
      setError("Select a business before saving.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      if (initialData?.id)
        await coaService.updateAccount(
          initialData.id,
          formData,
          organizationId,
        );
      else await coaService.createAccount(formData, organizationId);
      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || "Unable to save account.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={initialData ? "Edit account" : "New account"}
    >
      <div
        className="absolute inset-0 bg-slate-950/50"
        onClick={() => !loading && onClose()}
      />
      <div className="relative flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {initialData ? "Edit account" : "New ledger account"}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {initialData
                ? "Update the account directory."
                : "Create an account for this business."}
            </p>
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="space-y-5 overflow-y-auto px-6 py-5">
            {error && (
              <div className="flex gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                <FiAlertCircle className="mt-0.5 shrink-0" />
                {error}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Account type *</label>
                <select
                  className={inputClass}
                  required
                  value={formData.type}
                  onChange={(e) => {
                    const type = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      type,
                      sub_type: SUB_TYPES[type]?.[0] || "",
                    }));
                  }}
                >
                  {ACCOUNT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Detail type *</label>
                <select
                  className={inputClass}
                  required
                  value={formData.sub_type}
                  onChange={(e) => update("sub_type", e.target.value)}
                >
                  {detailTypes.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-600">
              <strong className="text-slate-800">
                Normal balance: {ACCOUNT_NORMAL_SIDE[formData.type] || "—"}.
              </strong>{" "}
              {formData.type === "Liability" &&
              formData.sub_type === "Credit Card"
                ? "Credit card purchases increase the liability with a credit; payments reduce it with a debit."
                : "Normal balance describes which side ordinarily increases this account. Actual balances come from posted ledger entries."}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Account code</label>
                <input
                  className={inputClass}
                  maxLength={40}
                  placeholder="1000"
                  value={formData.code}
                  onChange={(e) => update("code", e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <label className={labelClass}>Account name *</label>
                <input
                  className={inputClass}
                  required
                  maxLength={160}
                  placeholder="e.g. Operating Bank Account"
                  value={formData.name}
                  onChange={(e) => update("name", e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Account currency *</label>
              <select
                className={inputClass}
                value={formData.currency}
                onChange={(e) => update("currency", e.target.value)}
                required
              >
                {!currencyExists && (
                  <option value={formData.currency}>{formData.currency}</option>
                )}
                {CURRENCY_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.items.map(([code, name]) => (
                      <option key={code} value={code}>
                        {code} — {name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-slate-500">
                Defaults to the business currency ({organizationCurrency}).
                Multi-currency posting and conversion must be handled in the
                ledger.
              </p>
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea
                className={inputClass}
                rows={3}
                placeholder="What is this account used for?"
                value={formData.description}
                onChange={(e) => update("description", e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
            <button
              type="button"
              disabled={loading}
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !organizationId}
              className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading
                ? "Saving..."
                : initialData
                  ? "Save changes"
                  : "Create account"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}